import React, { useState, useEffect } from 'react';
import { 
  MegaphoneIcon,
  CalendarIcon,
  BellIcon,
  PlusIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  VideoCameraIcon,
  SpeakerWaveIcon,
  TvIcon,
  PencilIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import moment from 'moment';

import API_URL from '../config/api';

const MessageBoard = () => {
  const [activeTab, setActiveTab] = useState('messages');
  const [messages, setMessages] = useState([]);
  const [events, setEvents] = useState([]);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Modal states
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [showPageModal, setShowPageModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showEmergencyModal, setShowEmergencyModal] = useState(false);
  
  // Form states
  const [messageForm, setMessageForm] = useState({
    title: '',
    content: '',
    category: 'info',
    priority: 1,
    expires_at: ''
  });
  
  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    event_date: '',
    event_time: ''
  });
  
  const [pageForm, setPageForm] = useState({
    employee_name: '',
    message: '',
    page_type: 'text',
    priority: 'normal'
  });

  useEffect(() => {
    fetchAllData();
    // Auto-refresh every 10 seconds
    const interval = setInterval(fetchAllData, 10000);
    return () => clearInterval(interval);
  }, []);

  const fetchAllData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { 'Authorization': `Bearer ${token}` };

      const [messagesRes, eventsRes, pagesRes] = await Promise.all([
        fetch(`${API_URL}/api/message-board/messages`, { headers }),
        fetch(`${API_URL}/api/message-board/events`, { headers }),
        fetch(`${API_URL}/api/message-board/pages`, { headers })
      ]);

      if (messagesRes.ok) {
        const data = await messagesRes.json();
        setMessages(data.messages || []);
      }

      if (eventsRes.ok) {
        const data = await eventsRes.json();
        setEvents(data.events || []);
      }

      if (pagesRes.ok) {
        const data = await pagesRes.json();
        setPages(data.pages || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMessage = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/message-board/messages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(messageForm)
      });

      if (response.ok) {
        toast.success('Message created successfully!');
        setShowMessageModal(false);
        resetMessageForm();
        fetchAllData();
      } else {
        toast.error('Failed to create message');
      }
    } catch (error) {
      console.error('Error creating message:', error);
      toast.error('Error creating message');
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/message-board/events`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(eventForm)
      });

      if (response.ok) {
        toast.success('Event created successfully!');
        setShowEventModal(false);
        resetEventForm();
        fetchAllData();
      } else {
        toast.error('Failed to create event');
      }
    } catch (error) {
      console.error('Error creating event:', error);
      toast.error('Error creating event');
    }
  };

  const handlePageEmployee = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/message-board/pages`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(pageForm)
      });

      if (response.ok) {
        toast.success('Employee paged successfully!');
        setShowPageModal(false);
        resetPageForm();
        fetchAllData();
      } else {
        toast.error('Failed to page employee');
      }
    } catch (error) {
      console.error('Error paging employee:', error);
      toast.error('Error paging employee');
    }
  };

  const handleDeleteMessage = async (id) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/message-board/messages/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Message deleted');
        fetchAllData();
      } else {
        toast.error('Failed to delete message');
      }
    } catch (error) {
      toast.error('Error deleting message');
    }
  };

  const handleDeleteEvent = async (id) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/message-board/events/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Event deleted');
        fetchAllData();
      } else {
        toast.error('Failed to delete event');
      }
    } catch (error) {
      toast.error('Error deleting event');
    }
  };

  const resetMessageForm = () => {
    setMessageForm({
      title: '',
      content: '',
      category: 'info',
      priority: 1,
      expires_at: ''
    });
  };

  const resetEventForm = () => {
    setEventForm({
      title: '',
      description: '',
      event_date: '',
      event_time: ''
    });
  };

  const resetPageForm = () => {
    setPageForm({
      employee_name: '',
      message: '',
      page_type: 'text',
      priority: 'normal'
    });
  };

  const getCategoryColor = (category) => {
    const colors = {
      info: '#3b82f6',
      warning: '#f59e0b',
      urgent: '#ef4444',
      emergency: '#dc2626',
      announcement: '#8b5cf6'
    };
    return colors[category] || '#6b7280';
  };

  const getPriorityBadge = (priority) => {
    const badges = {
      1: { text: 'Normal', color: '#10b981' },
      2: { text: 'High', color: '#f59e0b' },
      3: { text: 'Urgent', color: '#ef4444' },
      4: { text: 'Emergency', color: '#dc2626' }
    };
    return badges[priority] || badges[1];
  };

  if (loading) {
    return <div className="spinner"></div>;
  }

  return (
    <div className="dashboard">
      {/* Page Header */}
      <div className="page-header" style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 className="page-title" style={{ fontSize: 28, fontWeight: 600, marginBottom: 8 }}>
              Message Board
            </h1>
            <p style={{ color: '#718096', fontSize: 14 }}>
              Manage announcements, events, and employee paging
            </p>
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            <button
              onClick={() => window.open('/display', '_blank')}
              style={{
                background: '#8b5cf6',
                color: 'white',
                padding: '10px 20px',
                borderRadius: 6,
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 14,
                fontWeight: 500
              }}
            >
              <TvIcon style={{ width: 18, height: 18 }} />
              View Display
            </button>
            <button
              onClick={() => setShowEmergencyModal(true)}
              style={{
                background: '#dc2626',
                color: 'white',
                padding: '10px 20px',
                borderRadius: 6,
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 14,
                fontWeight: 500
              }}
            >
              <ExclamationTriangleIcon style={{ width: 18, height: 18 }} />
              Emergency Alert
            </button>
          </div>
        </div>
      </div>

      {/* Quick Action Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20, marginBottom: 32 }}>
        <div 
          onClick={() => setShowMessageModal(true)}
          className="stat-card" 
          style={{ 
            background: 'white', 
            borderRadius: 8, 
            padding: 20, 
            display: 'flex', 
            alignItems: 'start', 
            gap: 16, 
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            cursor: 'pointer',
            transition: 'transform 0.2s, box-shadow 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
          }}
        >
          <div style={{ width: 56, height: 56, borderRadius: 12, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <BellIcon style={{ width: 28, height: 28, color: 'white' }} />
          </div>
          <div>
            <div style={{ fontSize: 28, fontWeight: 600, color: '#1f2937' }}>{messages.length}</div>
            <div style={{ fontSize: 14, color: '#4a5568', marginBottom: 4 }}>Active Messages</div>
            <div style={{ fontSize: 12, color: '#3b82f6' }}>+ Add New Message</div>
          </div>
        </div>

        <div 
          onClick={() => setShowEventModal(true)}
          className="stat-card" 
          style={{ 
            background: 'white', 
            borderRadius: 8, 
            padding: 20, 
            display: 'flex', 
            alignItems: 'start', 
            gap: 16, 
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            cursor: 'pointer',
            transition: 'transform 0.2s, box-shadow 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
          }}
        >
          <div style={{ width: 56, height: 56, borderRadius: 12, background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <CalendarIcon style={{ width: 28, height: 28, color: 'white' }} />
          </div>
          <div>
            <div style={{ fontSize: 28, fontWeight: 600, color: '#1f2937' }}>{events.length}</div>
            <div style={{ fontSize: 14, color: '#4a5568', marginBottom: 4 }}>Upcoming Events</div>
            <div style={{ fontSize: 12, color: '#10b981' }}>+ Add New Event</div>
          </div>
        </div>

        <div 
          onClick={() => setShowPageModal(true)}
          className="stat-card" 
          style={{ 
            background: 'white', 
            borderRadius: 8, 
            padding: 20, 
            display: 'flex', 
            alignItems: 'start', 
            gap: 16, 
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            cursor: 'pointer',
            transition: 'transform 0.2s, box-shadow 0.2s'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
          }}
        >
          <div style={{ width: 56, height: 56, borderRadius: 12, background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <MegaphoneIcon style={{ width: 28, height: 28, color: 'white' }} />
          </div>
          <div>
            <div style={{ fontSize: 28, fontWeight: 600, color: '#1f2937' }}>{pages.length}</div>
            <div style={{ fontSize: 14, color: '#4a5568', marginBottom: 4 }}>Active Pages</div>
            <div style={{ fontSize: 12, color: '#f59e0b' }}>+ Page Employee</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ borderBottom: '2px solid #e2e8f0', marginBottom: 24 }}>
        <div style={{ display: 'flex', gap: 32 }}>
          {[
            { id: 'messages', label: 'Messages', icon: BellIcon },
            { id: 'events', label: 'Events', icon: CalendarIcon },
            { id: 'pages', label: 'Pages', icon: MegaphoneIcon }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                background: 'none',
                border: 'none',
                padding: '12px 0',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 14,
                fontWeight: 500,
                color: activeTab === tab.id ? '#2B7FE0' : '#718096',
                borderBottom: activeTab === tab.id ? '2px solid #2B7FE0' : '2px solid transparent',
                marginBottom: -2
              }}
            >
              <tab.icon style={{ width: 18, height: 18 }} />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Messages Tab */}
      {activeTab === 'messages' && (
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Announcement Messages</h2>
            <button
              onClick={() => setShowMessageModal(true)}
              style={{
                background: '#2B7FE0',
                color: 'white',
                padding: '8px 16px',
                borderRadius: 6,
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 14
              }}
            >
              <PlusIcon style={{ width: 16, height: 16 }} />
              Add Message
            </button>
          </div>
          <div className="card-body">
            {messages.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#718096' }}>
                <BellIcon style={{ width: 48, height: 48, margin: '0 auto 16px', opacity: 0.5 }} />
                <p>No active messages. Create your first announcement!</p>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 16 }}>
                {messages.map(message => (
                  <div 
                    key={message.id}
                    style={{
                      padding: 16,
                      background: '#f7fafc',
                      borderRadius: 8,
                      borderLeft: `4px solid ${getCategoryColor(message.category)}`
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: 8 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <h3 style={{ fontSize: 16, fontWeight: 600 }}>{message.title}</h3>
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: 12,
                            fontSize: 11,
                            fontWeight: 600,
                            background: getPriorityBadge(message.priority).color,
                            color: 'white'
                          }}>
                            {getPriorityBadge(message.priority).text}
                          </span>
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: 12,
                            fontSize: 11,
                            fontWeight: 600,
                            background: getCategoryColor(message.category),
                            color: 'white',
                            textTransform: 'capitalize'
                          }}>
                            {message.category}
                          </span>
                        </div>
                        <p style={{ color: '#4a5568', fontSize: 14, marginBottom: 8 }}>{message.content}</p>
                        <div style={{ fontSize: 12, color: '#718096' }}>
                          Created by {message.created_by_name} • {moment(message.created_at).fromNow()}
                          {message.expires_at && ` • Expires ${moment(message.expires_at).fromNow()}`}
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteMessage(message.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: 8,
                          color: '#ef4444'
                        }}
                      >
                        <TrashIcon style={{ width: 18, height: 18 }} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Events Tab */}
      {activeTab === 'events' && (
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Upcoming Events</h2>
            <button
              onClick={() => setShowEventModal(true)}
              style={{
                background: '#2B7FE0',
                color: 'white',
                padding: '8px 16px',
                borderRadius: 6,
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 14
              }}
            >
              <PlusIcon style={{ width: 16, height: 16 }} />
              Add Event
            </button>
          </div>
          <div className="card-body">
            {events.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#718096' }}>
                <CalendarIcon style={{ width: 48, height: 48, margin: '0 auto 16px', opacity: 0.5 }} />
                <p>No upcoming events scheduled.</p>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 16 }}>
                {events.map(event => (
                  <div 
                    key={event.id}
                    style={{
                      padding: 16,
                      background: '#f7fafc',
                      borderRadius: 8,
                      display: 'flex',
                      gap: 16
                    }}
                  >
                    <div style={{
                      width: 80,
                      height: 80,
                      borderRadius: 8,
                      background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      flexShrink: 0
                    }}>
                      <div style={{ fontSize: 24, fontWeight: 600 }}>
                        {moment(event.event_date).format('DD')}
                      </div>
                      <div style={{ fontSize: 12 }}>
                        {moment(event.event_date).format('MMM')}
                      </div>
                    </div>
                    <div style={{ flex: 1 }}>
                      <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>{event.title}</h3>
                      {event.event_time && (
                        <div style={{ color: '#2B7FE0', fontSize: 14, marginBottom: 4 }}>
                          🕐 {moment(event.event_time, 'HH:mm:ss').format('h:mm A')}
                        </div>
                      )}
                      <p style={{ color: '#4a5568', fontSize: 14, marginBottom: 8 }}>{event.description}</p>
                      <div style={{ fontSize: 12, color: '#718096' }}>
                        Created by {event.created_by_name} • {moment(event.created_at).fromNow()}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDeleteEvent(event.id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 8,
                        color: '#ef4444',
                        height: 'fit-content'
                      }}
                    >
                      <TrashIcon style={{ width: 18, height: 18 }} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pages Tab */}
      {activeTab === 'pages' && (
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Active Page Requests</h2>
            <button
              onClick={() => setShowPageModal(true)}
              style={{
                background: '#2B7FE0',
                color: 'white',
                padding: '8px 16px',
                borderRadius: 6,
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 14
              }}
            >
              <PlusIcon style={{ width: 16, height: 16 }} />
              Page Employee
            </button>
          </div>
          <div className="card-body">
            {pages.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#718096' }}>
                <MegaphoneIcon style={{ width: 48, height: 48, margin: '0 auto 16px', opacity: 0.5 }} />
                <p>No active pages. Page an employee to get their attention!</p>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: 16 }}>
                {pages.map(page => (
                  <div 
                    key={page.id}
                    style={{
                      padding: 16,
                      background: page.priority === 'emergency' ? '#fee2e2' : '#f7fafc',
                      borderRadius: 8,
                      border: page.priority === 'emergency' ? '2px solid #ef4444' : 'none'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <h3 style={{ fontSize: 16, fontWeight: 600 }}>{page.employee_name}</h3>
                          <span style={{
                            padding: '2px 8px',
                            borderRadius: 12,
                            fontSize: 11,
                            fontWeight: 600,
                            background: page.priority === 'emergency' ? '#dc2626' : '#f59e0b',
                            color: 'white',
                            textTransform: 'capitalize'
                          }}>
                            {page.priority}
                          </span>
                          {page.page_type === 'video' && (
                            <VideoCameraIcon style={{ width: 16, height: 16, color: '#8b5cf6' }} />
                          )}
                          {page.page_type === 'audio' && (
                            <SpeakerWaveIcon style={{ width: 16, height: 16, color: '#3b82f6' }} />
                          )}
                        </div>
                        <p style={{ color: '#4a5568', fontSize: 14, marginBottom: 8 }}>{page.message}</p>
                        <div style={{ fontSize: 12, color: '#718096' }}>
                          Created {moment(page.created_at).fromNow()} • Expires {moment(page.expires_at).fromNow()}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Create Message Modal */}
      {showMessageModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: 8,
            width: '90%',
            maxWidth: 500,
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <div style={{
              padding: 20,
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{ fontSize: 20, fontWeight: 600 }}>Create Announcement</h2>
              <button
                onClick={() => {
                  setShowMessageModal(false);
                  resetMessageForm();
                }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8 }}
              >
                <XMarkIcon style={{ width: 24, height: 24 }} />
              </button>
            </div>

            <form onSubmit={handleCreateMessage} style={{ padding: 20 }}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>
                  Title
                </label>
                <input
                  type="text"
                  required
                  value={messageForm.title}
                  onChange={(e) => setMessageForm({ ...messageForm, title: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: 6,
                    fontSize: 14
                  }}
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>
                  Message
                </label>
                <textarea
                  required
                  rows={4}
                  value={messageForm.content}
                  onChange={(e) => setMessageForm({ ...messageForm, content: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: 6,
                    fontSize: 14,
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>
                    Category
                  </label>
                  <select
                    value={messageForm.category}
                    onChange={(e) => setMessageForm({ ...messageForm, category: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #e2e8f0',
                      borderRadius: 6,
                      fontSize: 14
                    }}
                  >
                    <option value="info">Info</option>
                    <option value="announcement">Announcement</option>
                    <option value="warning">Warning</option>
                    <option value="urgent">Urgent</option>
                    <option value="emergency">Emergency</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>
                    Priority
                  </label>
                  <select
                    value={messageForm.priority}
                    onChange={(e) => setMessageForm({ ...messageForm, priority: parseInt(e.target.value) })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #e2e8f0',
                      borderRadius: 6,
                      fontSize: 14
                    }}
                  >
                    <option value="1">Normal</option>
                    <option value="2">High</option>
                    <option value="3">Urgent</option>
                    <option value="4">Emergency</option>
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>
                  Expiration Date (Optional)
                </label>
                <input
                  type="datetime-local"
                  value={messageForm.expires_at}
                  onChange={(e) => setMessageForm({ ...messageForm, expires_at: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: 6,
                    fontSize: 14
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowMessageModal(false);
                    resetMessageForm();
                  }}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #e2e8f0',
                    borderRadius: 6,
                    background: 'white',
                    cursor: 'pointer',
                    fontSize: 14
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '8px 16px',
                    background: '#2B7FE0',
                    color: 'white',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: 14,
                    fontWeight: 500
                  }}
                >
                  Create Message
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create Event Modal */}
      {showEventModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: 8,
            width: '90%',
            maxWidth: 500,
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <div style={{
              padding: 20,
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{ fontSize: 20, fontWeight: 600 }}>Create Event</h2>
              <button
                onClick={() => {
                  setShowEventModal(false);
                  resetEventForm();
                }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8 }}
              >
                <XMarkIcon style={{ width: 24, height: 24 }} />
              </button>
            </div>

            <form onSubmit={handleCreateEvent} style={{ padding: 20 }}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>
                  Event Title
                </label>
                <input
                  type="text"
                  required
                  value={eventForm.title}
                  onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: 6,
                    fontSize: 14
                  }}
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>
                  Description
                </label>
                <textarea
                  rows={3}
                  value={eventForm.description}
                  onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: 6,
                    fontSize: 14,
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>
                    Date
                  </label>
                  <input
                    type="date"
                    required
                    value={eventForm.event_date}
                    onChange={(e) => setEventForm({ ...eventForm, event_date: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #e2e8f0',
                      borderRadius: 6,
                      fontSize: 14
                    }}
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>
                    Time (Optional)
                  </label>
                  <input
                    type="time"
                    value={eventForm.event_time}
                    onChange={(e) => setEventForm({ ...eventForm, event_time: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #e2e8f0',
                      borderRadius: 6,
                      fontSize: 14
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowEventModal(false);
                    resetEventForm();
                  }}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #e2e8f0',
                    borderRadius: 6,
                    background: 'white',
                    cursor: 'pointer',
                    fontSize: 14
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '8px 16px',
                    background: '#10b981',
                    color: 'white',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: 14,
                    fontWeight: 500
                  }}
                >
                  Create Event
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Page Employee Modal */}
      {showPageModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: 8,
            width: '90%',
            maxWidth: 500,
            maxHeight: '80vh',
            overflowY: 'auto'
          }}>
            <div style={{
              padding: 20,
              borderBottom: '1px solid #e2e8f0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{ fontSize: 20, fontWeight: 600 }}>Page Employee</h2>
              <button
                onClick={() => {
                  setShowPageModal(false);
                  resetPageForm();
                }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8 }}
              >
                <XMarkIcon style={{ width: 24, height: 24 }} />
              </button>
            </div>

            <form onSubmit={handlePageEmployee} style={{ padding: 20 }}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>
                  Employee Name
                </label>
                <input
                  type="text"
                  required
                  value={pageForm.employee_name}
                  onChange={(e) => setPageForm({ ...pageForm, employee_name: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: 6,
                    fontSize: 14
                  }}
                  placeholder="Enter employee name"
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>
                  Message
                </label>
                <textarea
                  required
                  rows={3}
                  value={pageForm.message}
                  onChange={(e) => setPageForm({ ...pageForm, message: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '1px solid #e2e8f0',
                    borderRadius: 6,
                    fontSize: 14,
                    resize: 'vertical'
                  }}
                  placeholder="Please come to the office"
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>
                    Page Type
                  </label>
                  <select
                    value={pageForm.page_type}
                    onChange={(e) => setPageForm({ ...pageForm, page_type: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #e2e8f0',
                      borderRadius: 6,
                      fontSize: 14
                    }}
                  >
                    <option value="text">Text Only</option>
                    <option value="audio">Audio</option>
                    <option value="video">Video</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>
                    Priority
                  </label>
                  <select
                    value={pageForm.priority}
                    onChange={(e) => setPageForm({ ...pageForm, priority: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #e2e8f0',
                      borderRadius: 6,
                      fontSize: 14
                    }}
                  >
                    <option value="normal">Normal</option>
                    <option value="urgent">Urgent</option>
                    <option value="emergency">Emergency</option>
                  </select>
                </div>
              </div>

              <div style={{ 
                background: '#fef3c7', 
                padding: 12, 
                borderRadius: 6, 
                marginBottom: 16,
                fontSize: 13,
                color: '#92400e'
              }}>
                ⏰ Page will expire after 5 minutes
              </div>

              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowPageModal(false);
                    resetPageForm();
                  }}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #e2e8f0',
                    borderRadius: 6,
                    background: 'white',
                    cursor: 'pointer',
                    fontSize: 14
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '8px 16px',
                    background: '#f59e0b',
                    color: 'white',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: 14,
                    fontWeight: 500
                  }}
                >
                  Send Page
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Emergency Alert Modal */}
      {showEmergencyModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: 8,
            width: '90%',
            maxWidth: 500,
            border: '4px solid #dc2626'
          }}>
            <div style={{
              padding: 20,
              background: '#dc2626',
              color: 'white',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <ExclamationTriangleIcon style={{ width: 24, height: 24 }} />
                <h2 style={{ fontSize: 20, fontWeight: 600 }}>Emergency Alert</h2>
              </div>
              <button
                onClick={() => setShowEmergencyModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8, color: 'white' }}
              >
                <XMarkIcon style={{ width: 24, height: 24 }} />
              </button>
            </div>

            <div style={{ padding: 20 }}>
              <p style={{ marginBottom: 16, color: '#4a5568' }}>
                This will create a high-priority emergency message that will be prominently displayed on all screens.
              </p>

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 14, fontWeight: 500 }}>
                  Emergency Message
                </label>
                <textarea
                  rows={4}
                  value={messageForm.content}
                  onChange={(e) => setMessageForm({ ...messageForm, content: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    border: '2px solid #dc2626',
                    borderRadius: 6,
                    fontSize: 14,
                    resize: 'vertical'
                  }}
                  placeholder="Enter emergency message..."
                />
              </div>

              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowEmergencyModal(false)}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #e2e8f0',
                    borderRadius: 6,
                    background: 'white',
                    cursor: 'pointer',
                    fontSize: 14
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!messageForm.content) {
                      toast.error('Please enter an emergency message');
                      return;
                    }
                    
                    try {
                      const token = localStorage.getItem('token');
                      const response = await fetch(`${API_URL}/api/message-board/messages`, {
                        method: 'POST',
                        headers: {
                          'Authorization': `Bearer ${token}`,
                          'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                          title: '🚨 EMERGENCY ALERT',
                          content: messageForm.content,
                          category: 'emergency',
                          priority: 4
                        })
                      });

                      if (response.ok) {
                        toast.success('Emergency alert broadcast!');
                        setShowEmergencyModal(false);
                        setMessageForm({ ...messageForm, content: '' });
                        fetchAllData();
                      }
                    } catch (error) {
                      toast.error('Failed to send emergency alert');
                    }
                  }}
                  style={{
                    padding: '8px 16px',
                    background: '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: 6,
                    cursor: 'pointer',
                    fontSize: 14,
                    fontWeight: 600
                  }}
                >
                  🚨 Broadcast Emergency
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageBoard;
